export type Lang = 'en' | 'es'

export const translations = {
  en: {
    // Navigation
    nav: {
      collection: 'Collection',
      ferbytouch: 'The FerbyTouch',
      testimonials: 'Testimonials',
      contact: 'Contact',
      shopCta: 'Shop the Collection',
      openMenu: 'Open menu',
      closeMenu: 'Close menu',
      language: 'Language',
    },

    // Hero
    hero: {
      badge: 'Handcrafted in Washington',
      titleLine1: "She's Going to Open",
      titleLine2: 'That Box',
      subtitle: "And You're Going to See a Smile You'll Never Forget.",
      intro:
        'You love her more than she\'ll ever fully understand. You want to give her things that show that. Things that say "you are special" — not just with words, but with beauty. With care. With something she can hold in her hands and feel.',
      shopCta: 'Shop the Collection',
      customCta: 'Request a Custom Piece',
    },

    // Problem
    problem: {
      heading: "But here's the problem:",
      main: "Everything out there looks like everyone else's.",
      body: 'The same mirrors. The same accessories. The same "pretty enough" gifts that end up forgotten on a shelf by the following weekend.',
      quote1: "You've settled before. We both know it.",
      quote2: 'Not this time.',
    },

    // What If
    whatIf: {
      heading1: 'What If Her Gift Was Genuinely',
      heading2: 'One of a Kind?',
      intro:
        'Not "limited edition" from a factory. Actually one of a kind — touched by real hands, decorated one pearl at a time, one crystal at a time, by an artisan who cared about every single detail.',
      intro2: 'What if you could give her something that:',
      features: [
        'No other girl in her school has',
        'Looks like it belongs in a fairy tale',
        'Was made specifically with her in mind',
        'Makes her feel like the most special girl in the world',
      ],
      tagline: "That's not a fantasy. That's FerbyStore.",
    },

    // Audience
    audience: {
      heading: 'This Is For You If...',
      items: [
        "You're a mom who wants to give her daughter something truly magical",
        "You're a young woman who's tired of having the same things as everyone else",
        "You're an aunt, godmother, or grandma who wants to show up with THE gift at her quinceañera, birthday, or graduation",
        "You're any woman who believes beautiful things deserve to be made with love — not manufactured by machines",
      ],
    },

    // Comparison
    comparison: {
      heading: 'Why Everything Else Disappoints',
      scrollHint: '← Scroll to see full table →',
      colTried: "What You've Tried",
      colShortfall: 'Why It Falls Short',
      rows: [
        {
          tried: 'Big box stores',
          shortfall: 'Millions of identical pieces — zero soul',
        },
        {
          tried: 'Amazon',
          shortfall: 'Arrives in a brown box with zero magic',
        },
        {
          tried: 'Generic boutiques',
          shortfall: 'Pretty packaging, forgettable product',
        },
        {
          tried: 'DIY',
          shortfall: "Time you don't have, results you can't guarantee",
        },
        {
          tried: 'Random Etsy sellers',
          shortfall: 'Inconsistent quality, no real personalization',
        },
      ],
      conclusion1:
        "The real problem isn't the options. It's the system behind them.",
      conclusion2:
        "Mass production doesn't care about your daughter. It doesn't know her name. It doesn't know her favorite color or the theme she's dreamed about for her room.",
      weDo: 'We do.',
    },

    // FerbyTouch
    ferbyTouch: {
      heading: 'Introducing The FerbyTouch™',
      intro:
        'The reason our pieces feel different — because they ARE different. Every FerbyStore piece is handcrafted using our signature layering process:',
      processes: [
        {
          title: 'Swarovski-style crystals',
          desc: 'Placed individually for maximum brilliance',
        },
        {
          title: 'Flat and round pearls',
          desc: 'In multiple sizes — for texture and elegance',
        },
        {
          title: 'Hand-molded clay figures',
          desc: 'Flowers, shapes, and characters created by actual human hands',
        },
        {
          title: 'Rhinestones and micro-details',
          desc: 'Because perfection lives in the small things',
        },
        {
          title: 'Hours of focused artisan attention',
          desc: 'Because art cannot be rushed',
        },
      ],
      conclusion:
        "The result is a piece you feel before you fully understand it. It's the kind of thing that makes a person stop. Look closer. Touch it gently. And quietly say:",
      quote: '"I have never seen anything like this."',
      customCard: {
        title: 'Customize Your Piece',
        desc: 'Have a specific dream in mind? Tell us your colors, theme, and personal details — we build it for her.',
        cta: 'Start Customizing',
      },
    },

    // Showcase
    showcase: {
      badge: 'Real Pieces, Made by Hand',
      heading: 'See the Craft in Every Detail',
      subheading: 'Each piece tells its own story',
      footer:
        'Every piece you see was decorated by hand, one detail at a time. No two are ever exactly alike.',
      labels: {
        princessPurse: 'Princess Purse',
        mirrorBagSet: 'Mirror & Bag Set',
        liloStitch: 'Lilo & Stitch Tumbler',
        princessGiftSet: 'Princess Gift Set',
        characterBow: 'Character Bow',
        animeTumbler: 'Anime Tumbler',
        glitterPearlPurse: 'Glitter Pearl Purse',
        castlePrincess: 'Castle Princess Purse',
        signatureBox: 'Signature Gift Box',
        kpopBow: 'K-Pop Bow',
        rainbowBow: 'Rainbow Sparkle Bow',
      },
    },

    // Collection
    collection: {
      heading: 'Our Handcrafted Collection',
      requestCta: 'Request this piece',
      mirrors: {
        title: 'Decorated Mirrors',
        subtitle: 'The Statement Piece',
        desc1:
          'Starting from a quality base mirror, our artisans transform every inch of the frame into a work of art — layering pearls, crystals, and clay details until it becomes something entirely new.',
        desc2:
          'For the little girl who wants something magical on her dresser. For the teenager who wants her room to look like a Pinterest board come to life. For the woman who appreciates beauty that was made, not manufactured.',
        feature:
          'Every mirror is fully personalizable — your colors, your theme, her name, her story.',
      },
      purses: {
        title: 'Decorated Purses',
        subtitle: 'Fashion Meets Art',
        desc1:
          'Our purses start as quality accessories and become something else entirely — hand-decorated with pearls, crystals, clay flowers, and exclusive details that turn a bag into a conversation piece.',
        quoteQ: 'Oh my gosh, where did you get that?',
        quoteA: "It's handmade. There's only one.",
        questionLabel: "The question you'll hear:",
        answerLabel: "The answer you'll love giving:",
        badge: 'Hand-decorated, one of a kind',
      },
      bows: {
        title: 'Handcrafted Bows',
        subtitle: 'The Perfect Finishing Touch',
        desc1:
          'Every bow we create is handmade with premium fabrics and ribbon — available in every style, size, and theme you can imagine.',
        desc2:
          'Sweet and colorful for the little ones. Bold and oversized for the teens. Elegant and refined for women who know that details matter.',
        badge: 'Endless styles & themes',
      },
      tumblers: {
        title: 'Custom Tumblers & Mugs',
        subtitle: 'Personalized to the Last Drop',
        desc1:
          'Names. Photos. Phrases. Artwork. Printed with professional sublimation technology that delivers vivid, lasting color.',
        desc2:
          "Perfect for: birthdays, baby showers, Mother's Day, Valentine's Day, graduations, and corporate gifts that actually impress.",
        badge: 'Any design you dream',
      },
      sets: {
        title: 'Custom Gift Sets',
        subtitle: 'When You Want to Give Everything',
        desc1:
          'Combine our handcrafted pieces into a curated gift set — beautifully packaged and ready to create a moment she will never forget.',
        desc2:
          'Arrives gift-ready. No extra wrapping needed. Pure magic in a box.',
        girlsSet: {
          name: "Girls' Set",
          items: 'Decorated mirror + bow + themed purse',
        },
        womensSet: {
          name: "Women's Set",
          items: 'Custom tumbler + decorated mirror + exclusive accessories',
        },
        badge: 'Arrives gift-ready in our signature box',
      },
    },

    // Testimonials
    testimonials: {
      heading1: 'Why Our Customers Come Back',
      heading2: 'Every Single Time',
      items: [
        {
          text: "I gave my daughter the decorated mirror for her birthday and she literally started crying. Her friends couldn't stop asking about it. FerbyStore is the only place I order from now.",
          name: 'Maria G.',
          location: 'Seattle WA',
        },
        {
          text: "I ordered a custom purse for my niece's quinceañera. The whole family was asking where I got it. I felt like the best tía in the room.",
          name: 'Claudia R.',
          location: 'Tacoma WA',
        },
        {
          text: "The tumbler I ordered for a coworker came out stunning. Professional finish, personal touch. I'll never buy generic gifts again.",
          name: 'Ashley M.',
          location: 'Puyallup WA',
        },
      ],
    },

    // Promise
    promise: {
      heading: 'Our Promise to You',
      body1: "We don't do average. We always do our best.",
      body2:
        "Every piece that leaves our studio in Puyallup, Washington has been inspected by hand, decorated with intention, and packaged with care — because it's going to someone who matters.",
      guarantee:
        "If your piece doesn't arrive exactly as described and agreed upon — we make it right. No questions. No hassle.",
      signature:
        "That's not a policy. That's a personal promise from the hands that made your piece.",
    },

    // Final CTA
    finalCta: {
      heading: 'You Have a Choice Right Now',
      intro1:
        'You can keep scrolling. Keep looking for something "good enough."',
      intro2:
        "Or you can give her something she'll still have on her dresser in ten years — something she'll show her own daughter someday and say:",
      quote: '"Someone loved me enough to have this made just for me."',
      tagline: "That's the FerbyStore difference.",
      cardsHeading: 'Ready to Create Her Moment?',
      cards: {
        shop: {
          title: 'Shop the Collection',
          desc: 'Handcrafted pieces — personalized for you — shipped to your door',
        },
        custom: {
          title: 'Request a Custom Piece',
          desc: "Tell us her colors, her theme, her dream — we'll build it.",
        },
        phone: {
          title: 'Call or Text Us Directly',
          desc: "We're real people. We answer.",
        },
        visit: {
          title: 'Follow Us on Instagram',
          addr1: '@ferby_store4',
          addr2: 'Behind the scenes & new pieces',
        },
      },
    },

    // Footer
    footer: {
      tagline: 'Handcrafted with love. Made to be remembered.',
      crafted: 'Crafted in Washington',
      exploreHeading: 'Explore',
      links: {
        collection: 'Our Collection',
        ferbytouch: 'The FerbyTouch™',
        stories: 'Customer Stories',
        promise: 'Our Promise',
      },
      contactHeading: 'Get in Touch',
      address: '1233 17th Ave SW\nPuyallup, WA 98371',
      copyright: '© {year} FerbyStore. All rights reserved.',
      privacy: 'Privacy Policy',
      terms: 'Terms of Service',
    },

    // Legal modal
    legal: {
      lastUpdated: 'Last updated',
      gotIt: 'Got it',
      close: 'Close',
      privacyTitle: 'Privacy Policy',
      termsTitle: 'Terms of Service',
      privacy: {
        intro:
          'At FerbyStore, your privacy matters as much as the care we put into every handcrafted piece. This Privacy Policy explains how we collect, use, and safeguard your information when you visit our website or place an order.',
        sections: [
          {
            title: 'Information We Collect',
            body: "We only collect the information needed to fulfill your order and stay in touch about your custom pieces: your name, email, phone number, shipping address, and any details you share about the design you'd like (themes, colors, names).",
          },
          {
            title: 'How We Use Your Information',
            list: [
              'To create, package, and ship your handcrafted pieces.',
              'To communicate updates about your custom order.',
              'To respond to questions you send us by email, phone, or text.',
              'To occasionally share new collections — only if you opt in.',
            ],
          },
          {
            title: 'Sharing Your Information',
            body: 'We never sell, rent, or trade your personal information. We only share details with trusted partners (like shipping carriers) strictly to deliver your order.',
          },
          {
            title: 'Photos & Personalization',
            body: 'If you send us a photo or artwork to be sublimated on a tumbler or printed on a custom piece, we use it only for your specific order and do not reuse it without your written permission.',
          },
          {
            title: 'Your Choices',
            body: 'You can request to view, update, or delete your information at any time by emailing us at ferbystore4@gmail.com.',
          },
          {
            title: 'Contact Us',
            body: 'Questions about your privacy? Reach out anytime — ferbystore4@gmail.com · 385-549-3420 · 1233 17th Ave SW, Puyallup, WA 98371.',
          },
        ],
      },
      terms: {
        intro:
          "Welcome to FerbyStore. By browsing our site or ordering a handcrafted piece, you agree to these Terms of Service. Please read them — they're written to protect both of us.",
        sections: [
          {
            title: 'Handmade & One of a Kind',
            body: 'Every piece is decorated by hand. Slight variations in pearl placement, crystal arrangement, or clay details are part of what makes your piece unique and should not be considered defects.',
          },
          {
            title: 'Orders & Custom Pieces',
            list: [
              'Custom pieces require approval of colors, theme, and personal details before we begin.',
              'Production times vary by piece (typically 1–4 weeks) and will be confirmed when you place your order.',
              'Once production has started on a custom piece, it cannot be cancelled.',
            ],
          },
          {
            title: 'Payment',
            body: 'Full payment (or an agreed deposit for custom work) is required before production begins. All prices are in USD.',
          },
          {
            title: 'Shipping',
            body: "Pieces are carefully packaged and shipped from our studio in Puyallup, WA. Shipping times depend on carrier and destination. FerbyStore is not responsible for carrier delays, but we'll help resolve any issue.",
          },
          {
            title: 'Our Promise',
            body: 'If your piece does not arrive exactly as described and agreed upon, we will make it right — no hassle. Contact us within 7 days of delivery with photos so we can quickly help.',
          },
          {
            title: 'Care Instructions',
            body: 'Handcrafted pieces are decorative. Avoid water, heat, and rough handling. Tumblers are hand-wash only — dishwashers and microwaves will damage the sublimation print.',
          },
          {
            title: 'Intellectual Property',
            body: "All photos, designs, and content on this site belong to FerbyStore. Please don't copy or reuse them without permission.",
          },
          {
            title: 'Contact',
            body: "Questions? We're real people who answer. ferbystore4@gmail.com · 385-549-3420.",
          },
        ],
      },
    },

    // Custom order modal
    customOrder: {
      title: 'Request a Custom Piece',
      subtitle: 'Tell us what you dream of — we will build it.',
      intro:
        'Fill in as much detail as you can. The more we know about her style, her theme, and the moment — the more magical the piece will be.',
      defaultSubject: 'Product Customization',
      fields: {
        fullName: { label: 'Your Full Name', placeholder: 'Maria Gonzalez' },
        email: { label: 'Email', placeholder: 'you@example.com' },
        phone: { label: 'Phone (optional)', placeholder: '801-716-0058' },
        productType: { label: 'Product Type', placeholder: 'Select a product' },
        productTypeOptions: [
          'Decorated Mirror',
          'Decorated Purse',
          'Handcrafted Bow',
          'Custom Tumbler',
          'Custom Gift Set',
          'Other',
        ],
        occasion: {
          label: 'Occasion',
          placeholder: 'Birthday, quinceañera, baby shower, just because...',
        },
        theme: {
          label: 'Theme / Style',
          placeholder: 'Princess, K-pop, Stitch, floral, elegant...',
        },
        colors: {
          label: 'Preferred Colors',
          placeholder: 'Soft pink, lavender, gold...',
        },
        personalization: {
          label: 'Name / Personal Touch',
          placeholder: 'Her name, a phrase, initials, a photo...',
        },
        budget: {
          label: 'Budget Range (optional)',
          placeholder: 'e.g. $50–$150',
        },
        deadline: {
          label: 'Needed By (optional)',
          placeholder: 'MM/DD/YYYY',
        },
        additionalNotes: {
          label: 'Additional Notes',
          placeholder: 'Anything else we should know to make it perfect?',
        },
      },
      submitButton: 'Send My Request',
      sendingLabel: 'Opening your email...',
      successTitle: 'Your email is ready',
      successBody:
        "We opened your email app with all your details pre-filled. Just press send and we'll get back to you within 24 hours.",
      errorTitle: 'Please complete the required fields',
      errorBody: 'Name, email, and product type are required.',
      required: 'Required',
      optional: 'Optional',
      close: 'Close',
    },

    // Purchase notifications
    notif: {
      justPurchased: 'just purchased',
      justNow: 'just now',
    },
  },

  es: {
    nav: {
      collection: 'Colección',
      ferbytouch: 'The FerbyTouch',
      testimonials: 'Testimonios',
      contact: 'Contacto',
      shopCta: 'Ver la Colección',
      openMenu: 'Abrir menú',
      closeMenu: 'Cerrar menú',
      language: 'Idioma',
    },

    hero: {
      badge: 'Hecho a Mano en Washington',
      titleLine1: 'Ella Va a Abrir',
      titleLine2: 'Esa Caja',
      subtitle: 'Y Vas a Ver una Sonrisa que Jamás Olvidarás.',
      intro:
        'La amas más de lo que ella jamás llegará a comprender. Quieres regalarle cosas que demuestren eso. Cosas que digan "eres especial" — no solo con palabras, sino con belleza. Con detalle. Con algo que pueda sostener en sus manos y sentir.',
      shopCta: 'Ver la Colección',
      customCta: 'Pedir una Pieza Personalizada',
    },

    problem: {
      heading: 'Pero aquí está el problema:',
      main: 'Todo lo que hay allá afuera se parece a lo de todos los demás.',
      body: 'Los mismos espejos. Los mismos accesorios. Los mismos regalos "bastante lindos" que terminan olvidados en una repisa al siguiente fin de semana.',
      quote1: 'Te has conformado antes. Ambos lo sabemos.',
      quote2: 'Esta vez no.',
    },

    whatIf: {
      heading1: '¿Y si su regalo fuera realmente',
      heading2: 'único en el mundo?',
      intro:
        'No "edición limitada" de una fábrica. Realmente único — tocado por manos reales, decorado una perla a la vez, un cristal a la vez, por una artesana que se preocupó por cada detalle.',
      intro2: '¿Y si pudieras regalarle algo que:',
      features: [
        'Ninguna otra niña en su escuela tiene',
        'Parece sacado de un cuento de hadas',
        'Fue hecho específicamente pensando en ella',
        'La hace sentir como la niña más especial del mundo',
      ],
      tagline: 'Eso no es una fantasía. Eso es FerbyStore.',
    },

    audience: {
      heading: 'Esto es para ti si...',
      items: [
        'Eres una mamá que quiere regalarle a su hija algo verdaderamente mágico',
        'Eres una joven cansada de tener lo mismo que todas las demás',
        'Eres una tía, madrina o abuelita que quiere llegar con EL regalo a su quinceañera, cumpleaños o graduación',
        'Eres cualquier mujer que cree que las cosas bellas merecen hacerse con amor — no fabricarse en máquinas',
      ],
    },

    comparison: {
      heading: 'Por Qué Todo lo Demás Decepciona',
      scrollHint: '← Desliza para ver la tabla completa →',
      colTried: 'Lo Que Has Probado',
      colShortfall: 'Por Qué Falla',
      rows: [
        {
          tried: 'Tiendas grandes',
          shortfall: 'Millones de piezas idénticas — cero alma',
        },
        {
          tried: 'Amazon',
          shortfall: 'Llega en una caja café sin nada de magia',
        },
        {
          tried: 'Boutiques genéricas',
          shortfall: 'Empaque bonito, producto olvidable',
        },
        {
          tried: 'DIY (hazlo tú)',
          shortfall:
            'Tiempo que no tienes, resultados que no puedes garantizar',
        },
        {
          tried: 'Vendedores de Etsy al azar',
          shortfall: 'Calidad inconsistente, sin personalización real',
        },
      ],
      conclusion1:
        'El verdadero problema no son las opciones. Es el sistema detrás de ellas.',
      conclusion2:
        'La producción en masa no se preocupa por tu hija. No sabe su nombre. No sabe su color favorito o el tema que ha soñado para su cuarto.',
      weDo: 'Nosotros sí.',
    },

    ferbyTouch: {
      heading: 'Te Presentamos The FerbyTouch™',
      intro:
        'La razón por la que nuestras piezas se sienten diferentes — porque SÍ son diferentes. Cada pieza de FerbyStore es hecha a mano usando nuestro proceso de capas característico:',
      processes: [
        {
          title: 'Cristales tipo Swarovski',
          desc: 'Colocados uno por uno para máximo brillo',
        },
        {
          title: 'Perlas planas y redondas',
          desc: 'En varios tamaños — para textura y elegancia',
        },
        {
          title: 'Figuras de arcilla moldeadas a mano',
          desc: 'Flores, formas y personajes creados por manos humanas reales',
        },
        {
          title: 'Pedrería y micro-detalles',
          desc: 'Porque la perfección vive en las cosas pequeñas',
        },
        {
          title: 'Horas de atención artesanal',
          desc: 'Porque el arte no se puede apresurar',
        },
      ],
      conclusion:
        'El resultado es una pieza que sientes antes de comprenderla por completo. Es la clase de cosa que hace a alguien detenerse. Acercarse. Tocarla con cuidado. Y decir en voz baja:',
      quote: '"Nunca había visto algo así."',
      customCard: {
        title: 'Personaliza Tu Pieza',
        desc: '¿Tienes un sueño específico en mente? Dinos tus colores, tema y detalles personales — nosotros la construimos para ella.',
        cta: 'Empezar a Personalizar',
      },
    },

    showcase: {
      badge: 'Piezas Reales, Hechas a Mano',
      heading: 'Mira el Arte en Cada Detalle',
      subheading: 'Cada pieza cuenta su propia historia',
      footer:
        'Cada pieza que ves fue decorada a mano, un detalle a la vez. No hay dos exactamente iguales.',
      labels: {
        princessPurse: 'Bolsa Princesa',
        mirrorBagSet: 'Set Espejo y Bolsa',
        liloStitch: 'Termo Lilo y Stitch',
        princessGiftSet: 'Set de Regalo Princesa',
        characterBow: 'Moño de Personajes',
        animeTumbler: 'Termo Anime',
        glitterPearlPurse: 'Bolsa Brillo y Perlas',
        castlePrincess: 'Bolsa Princesa Castillo',
        signatureBox: 'Caja Regalo Signature',
        kpopBow: 'Moño K-Pop',
        rainbowBow: 'Moño Arcoíris Brillante',
      },
    },

    collection: {
      heading: 'Nuestra Colección Hecha a Mano',
      requestCta: 'Pedir esta pieza',
      mirrors: {
        title: 'Espejos Decorados',
        subtitle: 'La Pieza Estrella',
        desc1:
          'Partiendo de un espejo base de calidad, nuestras artesanas transforman cada centímetro del marco en una obra de arte — capa por capa de perlas, cristales y detalles de arcilla, hasta convertirse en algo totalmente nuevo.',
        desc2:
          'Para la niña pequeña que quiere algo mágico en su tocador. Para la adolescente que quiere que su cuarto se vea como un tablero de Pinterest hecho realidad. Para la mujer que aprecia la belleza que se hizo, no que se fabricó.',
        feature:
          'Cada espejo es totalmente personalizable — tus colores, tu tema, su nombre, su historia.',
      },
      purses: {
        title: 'Bolsas Decoradas',
        subtitle: 'Moda Hecha Arte',
        desc1:
          'Nuestras bolsas comienzan como accesorios de calidad y se convierten en algo completamente distinto — decoradas a mano con perlas, cristales, flores de arcilla y detalles exclusivos que transforman una bolsa en una pieza de conversación.',
        quoteQ: 'Oh por Dios, ¿de dónde la sacaste?',
        quoteA: 'Es hecha a mano. Solo hay una.',
        questionLabel: 'La pregunta que vas a escuchar:',
        answerLabel: 'La respuesta que te encantará dar:',
        badge: 'Decorada a mano, única en su tipo',
      },
      bows: {
        title: 'Moños Hechos a Mano',
        subtitle: 'El Toque Final Perfecto',
        desc1:
          'Cada moño que creamos está hecho a mano con telas y listones premium — disponibles en cada estilo, tamaño y tema que puedas imaginar.',
        desc2:
          'Dulces y coloridos para las más pequeñas. Atrevidos y grandes para las adolescentes. Elegantes y refinados para mujeres que saben que los detalles importan.',
        badge: 'Estilos y temas sin límite',
      },
      tumblers: {
        title: 'Termos y Tazas Personalizadas',
        subtitle: 'Personalizado Hasta la Última Gota',
        desc1:
          'Nombres. Fotos. Frases. Arte. Impresos con tecnología profesional de sublimación que entrega color vibrante y duradero.',
        desc2:
          'Perfectos para: cumpleaños, baby showers, Día de las Madres, San Valentín, graduaciones y regalos corporativos que sí impresionan.',
        badge: 'Cualquier diseño que sueñes',
      },
      sets: {
        title: 'Sets de Regalo Personalizados',
        subtitle: 'Cuando Quieres Regalarlo Todo',
        desc1:
          'Combina nuestras piezas hechas a mano en un set de regalo curado — bellamente empacado y listo para crear un momento que ella nunca olvidará.',
        desc2:
          'Llega listo para regalar. Sin envoltura extra. Pura magia en una caja.',
        girlsSet: {
          name: 'Set para Niñas',
          items: 'Espejo decorado + moño + bolsa temática',
        },
        womensSet: {
          name: 'Set para Mujeres',
          items:
            'Termo personalizado + espejo decorado + accesorios exclusivos',
        },
        badge: 'Llega listo para regalar en nuestra caja signature',
      },
    },

    testimonials: {
      heading1: 'Por Qué Nuestras Clientas Regresan',
      heading2: 'Cada Vez',
      items: [
        {
          text: 'Le regalé a mi hija el espejo decorado para su cumpleaños y literalmente comenzó a llorar. Sus amigas no dejaron de preguntar sobre él. FerbyStore es el único lugar donde compro ahora.',
          name: 'Maria G.',
          location: 'Seattle WA',
        },
        {
          text: 'Pedí una bolsa personalizada para los quince de mi sobrina. Toda la familia preguntaba dónde la conseguí. Me sentí como la mejor tía del salón.',
          name: 'Claudia R.',
          location: 'Tacoma WA',
        },
        {
          text: 'El termo que pedí para una compañera de trabajo quedó precioso. Acabado profesional, toque personal. Nunca más volveré a comprar regalos genéricos.',
          name: 'Ashley M.',
          location: 'Puyallup WA',
        },
      ],
    },

    promise: {
      heading: 'Nuestra Promesa a Ti',
      body1: 'No hacemos lo promedio. Siempre hacemos lo mejor.',
      body2:
        'Cada pieza que sale de nuestro estudio en Puyallup, Washington ha sido inspeccionada a mano, decorada con intención y empacada con cuidado — porque va para alguien que importa.',
      guarantee:
        'Si tu pieza no llega exactamente como fue descrita y acordada — lo arreglamos. Sin preguntas. Sin trámites.',
      signature:
        'Eso no es una política. Es una promesa personal de las manos que hicieron tu pieza.',
    },

    finalCta: {
      heading: 'Tienes una Decisión Ahora Mismo',
      intro1:
        'Puedes seguir desplazándote. Seguir buscando algo "bastante bueno".',
      intro2:
        'O puedes regalarle algo que aún tendrá en su tocador en diez años — algo que algún día le mostrará a su propia hija y dirá:',
      quote:
        '"Alguien me amó lo suficiente para mandarlo a hacer solo para mí."',
      tagline: 'Esa es la diferencia de FerbyStore.',
      cardsHeading: '¿Lista para Crear Su Momento?',
      cards: {
        shop: {
          title: 'Ver la Colección',
          desc: 'Piezas hechas a mano — personalizadas para ti — entregadas en tu puerta',
        },
        custom: {
          title: 'Pedir una Pieza Personalizada',
          desc: 'Dinos sus colores, su tema, su sueño — lo construimos.',
        },
        phone: {
          title: 'Llámanos o Envía un Texto',
          desc: 'Somos personas reales. Contestamos.',
        },
        visit: {
          title: 'Síguenos en Instagram',
          addr1: '@ferby_store4',
          addr2: 'Detrás de cámaras y piezas nuevas',
        },
      },
    },

    footer: {
      tagline: 'Hecho a mano con amor. Pensado para recordarse.',
      crafted: 'Hecho en Washington',
      exploreHeading: 'Explora',
      links: {
        collection: 'Nuestra Colección',
        ferbytouch: 'The FerbyTouch™',
        stories: 'Historias de Clientas',
        promise: 'Nuestra Promesa',
      },
      contactHeading: 'Contáctanos',
      address: '1233 17th Ave SW\nPuyallup, WA 98371',
      copyright: '© {year} FerbyStore. Todos los derechos reservados.',
      privacy: 'Política de Privacidad',
      terms: 'Términos de Servicio',
    },

    legal: {
      lastUpdated: 'Última actualización',
      gotIt: 'Entendido',
      close: 'Cerrar',
      privacyTitle: 'Política de Privacidad',
      termsTitle: 'Términos de Servicio',
      privacy: {
        intro:
          'En FerbyStore, tu privacidad importa tanto como el cuidado que ponemos en cada pieza hecha a mano. Esta Política de Privacidad explica cómo recopilamos, usamos y protegemos tu información cuando visitas nuestro sitio o haces un pedido.',
        sections: [
          {
            title: 'Información que Recopilamos',
            body: 'Solo recopilamos la información necesaria para cumplir con tu pedido y mantenernos en contacto sobre tus piezas personalizadas: tu nombre, correo electrónico, número de teléfono, dirección de envío y cualquier detalle que compartas sobre el diseño que deseas (temas, colores, nombres).',
          },
          {
            title: 'Cómo Usamos tu Información',
            list: [
              'Para crear, empacar y enviar tus piezas hechas a mano.',
              'Para comunicarte actualizaciones sobre tu pedido personalizado.',
              'Para responder a preguntas que envíes por correo, teléfono o texto.',
              'Para compartir ocasionalmente nuevas colecciones — solo si das tu permiso.',
            ],
          },
          {
            title: 'Compartir tu Información',
            body: 'Nunca vendemos, rentamos o intercambiamos tu información personal. Solo compartimos detalles con socios de confianza (como los servicios de envío) estrictamente para entregar tu pedido.',
          },
          {
            title: 'Fotos y Personalización',
            body: 'Si nos envías una foto o arte para sublimar en un termo o imprimir en una pieza personalizada, solo lo usamos para tu pedido específico y no lo reutilizamos sin tu permiso por escrito.',
          },
          {
            title: 'Tus Opciones',
            body: 'Puedes solicitar ver, actualizar o eliminar tu información en cualquier momento escribiéndonos a ferbystore4@gmail.com.',
          },
          {
            title: 'Contáctanos',
            body: '¿Preguntas sobre tu privacidad? Escríbenos cuando quieras — ferbystore4@gmail.com · 385-549-3420 · 1233 17th Ave SW, Puyallup, WA 98371.',
          },
        ],
      },
      terms: {
        intro:
          'Bienvenida a FerbyStore. Al navegar nuestro sitio o pedir una pieza hecha a mano, aceptas estos Términos de Servicio. Por favor léelos — están escritos para protegernos a ambas.',
        sections: [
          {
            title: 'Hecho a Mano y Único',
            body: 'Cada pieza es decorada a mano. Pequeñas variaciones en la colocación de perlas, el arreglo de cristales o los detalles de arcilla son parte de lo que hace única tu pieza y no deben considerarse defectos.',
          },
          {
            title: 'Pedidos y Piezas Personalizadas',
            list: [
              'Las piezas personalizadas requieren aprobación de colores, tema y detalles personales antes de comenzar.',
              'Los tiempos de producción varían por pieza (típicamente 1–4 semanas) y se confirmarán al hacer tu pedido.',
              'Una vez iniciada la producción de una pieza personalizada, no se puede cancelar.',
            ],
          },
          {
            title: 'Pago',
            body: 'Se requiere el pago completo (o un depósito acordado para trabajo personalizado) antes de iniciar la producción. Todos los precios están en USD.',
          },
          {
            title: 'Envíos',
            body: 'Las piezas se empacan cuidadosamente y se envían desde nuestro estudio en Puyallup, WA. Los tiempos de envío dependen de la paquetería y el destino. FerbyStore no se hace responsable por retrasos de la paquetería, pero te ayudamos a resolver cualquier problema.',
          },
          {
            title: 'Nuestra Promesa',
            body: 'Si tu pieza no llega exactamente como fue descrita y acordada, lo arreglamos — sin trámites. Contáctanos dentro de 7 días después de la entrega con fotos para ayudarte rápidamente.',
          },
          {
            title: 'Instrucciones de Cuidado',
            body: 'Las piezas hechas a mano son decorativas. Evita el agua, el calor y el manejo brusco. Los termos se lavan solo a mano — los lavavajillas y microondas dañan la impresión de sublimación.',
          },
          {
            title: 'Propiedad Intelectual',
            body: 'Todas las fotos, diseños y contenido de este sitio pertenecen a FerbyStore. Por favor no los copies ni los reutilices sin permiso.',
          },
          {
            title: 'Contacto',
            body: '¿Preguntas? Somos personas reales que contestamos. ferbystore4@gmail.com · 385-549-3420.',
          },
        ],
      },
    },

    customOrder: {
      title: 'Pedir una Pieza Personalizada',
      subtitle: 'Cuéntanos con qué sueñas — nosotros lo construimos.',
      intro:
        'Llena todos los detalles que puedas. Mientras más sepamos sobre su estilo, su tema y el momento — más mágica será la pieza.',
      defaultSubject: 'Customización de Producto',
      fields: {
        fullName: {
          label: 'Tu Nombre Completo',
          placeholder: 'María González',
        },
        email: { label: 'Correo Electrónico', placeholder: 'tu@correo.com' },
        phone: { label: 'Teléfono (opcional)', placeholder: '801-716-0058' },
        productType: {
          label: 'Tipo de Producto',
          placeholder: 'Selecciona un producto',
        },
        productTypeOptions: [
          'Espejo Decorado',
          'Bolsa Decorada',
          'Moño Hecho a Mano',
          'Termo Personalizado',
          'Set de Regalo Personalizado',
          'Otro',
        ],
        occasion: {
          label: 'Ocasión',
          placeholder:
            'Cumpleaños, quinceañera, baby shower, solo porque sí...',
        },
        theme: {
          label: 'Tema / Estilo',
          placeholder: 'Princesa, K-pop, Stitch, floral, elegante...',
        },
        colors: {
          label: 'Colores Preferidos',
          placeholder: 'Rosa pastel, lavanda, dorado...',
        },
        personalization: {
          label: 'Nombre / Toque Personal',
          placeholder: 'Su nombre, una frase, iniciales, una foto...',
        },
        budget: {
          label: 'Rango de Presupuesto (opcional)',
          placeholder: 'ej. $50–$150',
        },
        deadline: {
          label: 'Lo Necesitas Para (opcional)',
          placeholder: 'DD/MM/AAAA',
        },
        additionalNotes: {
          label: 'Notas Adicionales',
          placeholder: '¿Algo más que debamos saber para hacerla perfecta?',
        },
      },
      submitButton: 'Enviar Mi Solicitud',
      sendingLabel: 'Abriendo tu correo...',
      successTitle: 'Tu correo está listo',
      successBody:
        'Abrimos tu aplicación de correo con todos tus detalles ya llenados. Solo presiona enviar y te respondemos dentro de 24 horas.',
      errorTitle: 'Por favor completa los campos requeridos',
      errorBody: 'El nombre, correo y tipo de producto son obligatorios.',
      required: 'Obligatorio',
      optional: 'Opcional',
      close: 'Cerrar',
    },

    notif: {
      justPurchased: 'acaba de comprar',
      justNow: 'ahora mismo',
    },
  },
} as const

export type Translation = (typeof translations)['en']
